import { z } from "zod";
import {
  BRAZIL_UFS,
  MAX_DOCUMENT_CENTS,
  MAX_PRICE_CENTS,
  MAX_STOCK_QTY_Q4,
  NFE_MAX_ITEMS,
  NFE_VOID_MAX_RANGE,
  eventTextProblem,
  normalizeEventText,
  voidRangeProblem,
  isValidCNPJ,
  isValidCPF,
  normalizeDocument,
  type NfeGap,
  type TaxTotals,
} from "@cx/domain";
import { listQuery, type ListResult } from "./common";
import { text } from "./fields";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";
import type { FiscalProviderId } from "./fiscal";

// ===== Notas fiscais eletrônicas (T2.3, RF-VEN-03): NF-e modelo 55 de saída =====

export const FISCAL_DOCUMENT_STATUSES = [
  "draft",
  "queued",
  "sending",
  "authorized",
  "rejected",
  "denied",
  "canceled",
  "voided",
] as const;
export type FiscalDocumentStatus = (typeof FISCAL_DOCUMENT_STATUSES)[number];
/** Em envio: a tela acompanha até sair o resultado. */
export const IN_FLIGHT_STATUSES: readonly FiscalDocumentStatus[] = ["queued", "sending"];
export const NFE_FREIGHT_MODES = [0, 1, 2, 3, 4, 9] as const;
export const NFE_PRESENCE = [0, 1, 2, 3, 4, 5, 9] as const;
export const NFE_MAX_PAYMENTS = 120;
export const NFE_MAX_VOLUMES = 20;

const cents = (what: string, max = MAX_DOCUMENT_CENTS) =>
  z
    .number(`Informe ${what}`)
    .int("Use centavos inteiros")
    .min(0, "Valor não pode ser negativo")
    .max(max, "Valor acima do limite");
const grams = z
  .number()
  .int("Peso em gramas inteiros")
  .min(0)
  .max(1_000_000_000)
  .nullish()
  .transform((v) => v ?? null);

export const nfeVolumeInput = z.object({
  quantity: z
    .number()
    .int()
    .min(0)
    .max(999_999_999)
    .nullish()
    .transform((v) => v ?? null),
  species: text(60),
  brand: text(60),
  numbering: text(60),
  netWeightG: grams,
  grossWeightG: grams,
});

export const nfeTransportInput = z.object({
  mode: z.literal(NFE_FREIGHT_MODES, "Modalidade do frete inválida").default(9),
  carrierId: ulidParam.nullish().transform((v) => v ?? null),
  vehiclePlate: text(8).pipe(
    z
      .string()
      .regex(/^[A-Z]{3}-?\d[A-Z0-9]\d{2}$/i, "Placa inválida")
      .nullable(),
  ),
  vehicleUf: z
    .enum(BRAZIL_UFS, "UF inválida")
    .nullish()
    .transform((v) => v ?? null),
  vehicleRntc: text(20),
  volumes: z.array(nfeVolumeInput).max(NFE_MAX_VOLUMES, `Até ${NFE_MAX_VOLUMES} volumes`).default([]),
});
export type NfeTransportInput = z.output<typeof nfeTransportInput>;

export const nfeItemInput = z.object({
  productId: ulidParam,
  /** null = nome do produto. */
  description: text(120),
  qtyQ4: z.number().int().min(1, "Quantidade deve ser maior que zero").max(MAX_STOCK_QTY_Q4),
  unitPriceCents: cents("o preço", MAX_PRICE_CENTS),
  discountCents: cents("o desconto").default(0),
});

export const nfePaymentInput = z.object({
  paymentMethodId: ulidParam.nullish().transform((v) => v ?? null),
  dueDate: businessDate,
  amountCents: cents("o valor"),
});

const authorizedDoc = z
  .string()
  .trim()
  .max(20)
  .transform((v) => normalizeDocument(v))
  .refine((v) => (v.length === 11 ? isValidCPF(v) : isValidCNPJ(v)), "CPF/CNPJ inválido");

/** Rascunho da NF-e (avulsa ou edição do rascunho/rejeitada). Impostos sempre recalculados pelo motor. */
export const fiscalDocumentInput = z.object({
  taxOperationId: ulidParam.nullish().transform((v) => v ?? null),
  contactId: ulidParam,
  presenceIndicator: z.literal(NFE_PRESENCE, "Indicador de presença inválido").default(1),
  exitAt: z.iso
    .datetime({ offset: true })
    .nullish()
    .transform((v) => v ?? null),
  items: z
    .array(nfeItemInput)
    .min(1, "Inclua ao menos um item")
    .max(NFE_MAX_ITEMS, `A nota aceita até ${NFE_MAX_ITEMS} itens`),
  freightCents: cents("o frete").default(0),
  insuranceCents: cents("o seguro").default(0),
  otherExpensesCents: cents("outras despesas").default(0),
  discountCents: cents("o desconto").default(0),
  transport: nfeTransportInput.default({
    mode: 9,
    carrierId: null,
    vehiclePlate: null,
    vehicleUf: null,
    vehicleRntc: null,
    volumes: [],
  }),
  payments: z.array(nfePaymentInput).max(NFE_MAX_PAYMENTS, `Até ${NFE_MAX_PAYMENTS} parcelas`).default([]),
  /** null = texto da natureza de operação. */
  complementaryInfo: text(5000),
  fiscoInfo: text(2000),
  authorizedXml: z.array(authorizedDoc).max(10, "Até 10 autorizados").default([]),
});
export type FiscalDocumentInput = z.output<typeof fiscalDocumentInput>;
export type FiscalDocumentInputBody = z.input<typeof fiscalDocumentInput>;

export const invoiceSalesOrderInput = z.object({
  taxOperationId: ulidParam.nullish().transform((v) => v ?? null),
  /** Forma de pagamento das parcelas do pedido (tPag da NF-e; o pedido não guarda forma por parcela). */
  paymentMethodId: ulidParam.nullish().transform((v) => v ?? null),
});
export type InvoiceSalesOrderInput = z.output<typeof invoiceSalesOrderInput>;

export const fiscalDocumentListQuery = listQuery.extend({
  status: z.enum(FISCAL_DOCUMENT_STATUSES).optional(),
});
export type FiscalDocumentListQuery = z.output<typeof fiscalDocumentListQuery>;

export type FiscalDocumentOrigin =
  | { type: "sales_order"; id: string; number: number | null }
  /** Devolução (T2.4): a NF-e devolvida. */
  | { type: "fiscal_document"; id: string; number: number | null }
  /** Faturamento agrupado (T2.7): a NF-e de vários pedidos do mesmo cliente. */
  | { type: "grouped_billing"; id: string; number: number | null }
  /** Ordem de serviço (T2.9): a NF-e das peças. */
  | { type: "service_order"; id: string; number: number | null }
  | null;

export type FiscalDocumentListItem = {
  id: string;
  model: string;
  series: number | null;
  number: number | null;
  status: FiscalDocumentStatus;
  accessKey: string | null;
  recipientName: string;
  recipientDocument: string | null;
  totalCents: number;
  environment: 1 | 2;
  issuedAt: string | null;
  authorizedAt: string | null;
  createdAt: string;
  origin: FiscalDocumentOrigin;
  rejectionCode: string | null;
  rejectionReason: string | null;
};
export type FiscalDocumentList = ListResult<FiscalDocumentListItem>;

export type FiscalDocumentItemView = {
  position: number;
  productId: string | null;
  code: string | null;
  description: string;
  ncm: string | null;
  cfop: string;
  unit: string;
  qtyQ4: number;
  unitPriceCents: number;
  grossCents: number;
  discountCents: number;
  icmsCents: number;
  stCents: number;
  ipiCents: number;
  totalCents: number;
};

export type FiscalDocumentView = FiscalDocumentListItem & {
  natureName: string | null;
  taxOperationId: string | null;
  presenceIndicator: number | null;
  protocol: string | null;
  statusMessage: string | null;
  attempt: number;
  contactId: string | null;
  recipient: {
    document: string;
    name: string;
    ieIndicator: number;
    ie: string | null;
    email: string | null;
    address: string;
  } | null;
  items: FiscalDocumentItemView[];
  totals: TaxTotals | null;
  transport: NfeTransportInput;
  payments: {
    paymentMethodId: string | null;
    name: string;
    sefazType: string | null;
    dueDate: string;
    amountCents: number;
  }[];
  complementaryInfo: string | null;
  fiscoInfo: string | null;
  hasXml: boolean;
  hasDanfe: boolean;
  emailSentAt: string | null;
  effectsError: string | null;
  /** Rascunho ou rejeitada: dá para editar e enviar. */
  editable: boolean;
  /** "Consultar de novo" disponível: a rodada anterior terminou pendente ou ficou parada (T2.3, revisão). */
  canRefresh: boolean;
  /** Entrada que recria o rascunho (para o formulário de edição); null na devolução (não se edita). */
  input: FiscalDocumentInput | null;
  /** finNFe (1 normal, 4 devolução) e tpNF (out = saída, in = entrada). */
  purpose: number;
  direction: "out" | "in";
  /** NF-e referenciadas (NFref): a nota devolvida, quando é dela. */
  references: { id: string | null; accessKey: string; number: number | null; series: number | null }[];
  /** Devoluções desta nota (qualquer situação). */
  returns: {
    id: string;
    number: number | null;
    series: number | null;
    status: FiscalDocumentStatus;
    totalCents: number;
  }[];
  /** Eventos (cancelamento, CC-e, inutilização do número), do mais novo ao mais antigo. */
  events: FiscalEventView[];
  /** Saldo devolvível por item (posição → quantidade q4); vazio quando a nota não aceita devolução. */
  returnable: { position: number; qtyQ4: number }[];
  /** Fim do prazo do cancelamento/CC-e (ISO); null = não se aplica. */
  cancelUntil: string | null;
  correctionUntil: string | null;
  /** CC-e vigente (a de maior sequência registrada). */
  currentCorrection: string | null;
};

export const NFE_ENVIRONMENTS = [1, 2] as const;
export const nfeSettingsInput = z.object({
  environment: z.literal(NFE_ENVIRONMENTS, "Ambiente inválido"),
  series: z
    .number("Informe a série")
    .int("Série inválida")
    .min(0, "Série de 0 a 999")
    .max(999, "Série de 0 a 999")
    .refine((s) => s < 890 || s > 899, "Séries 890 a 899 são do Fisco"),
  nextNumber: z.number("Informe o próximo número").int().min(1, "Número de 1 a 999.999.999").max(999_999_999),
  autoEmail: z.boolean().default(true),
});
export type NfeSettingsInput = z.output<typeof nfeSettingsInput>;
export type NfeSettings = NfeSettingsInput & {
  provider: FiscalProviderId;
  /** Maior número já usado na série (0 = nenhum). */
  lastUsedNumber: number;
};

export type FiscalFileLink = { url: string; expiresInS: number };

// ===== Eventos da NF-e (T2.4): cancelamento, carta de correção, inutilização e devolução =====

export const FISCAL_EVENT_KINDS = ["cancel", "correction", "void_range"] as const;
export type FiscalEventKind = (typeof FISCAL_EVENT_KINDS)[number];
export const FISCAL_EVENT_STATUSES = ["pending", "sending", "registered", "rejected"] as const;
export type FiscalEventStatus = (typeof FISCAL_EVENT_STATUSES)[number];

const eventText = (kind: "reason" | "correction") =>
  z
    .string(kind === "reason" ? "Informe a justificativa" : "Informe a correção")
    .max(4000, "Texto longo demais")
    .superRefine((v, c) => {
      const p = eventTextProblem(v, kind);
      if (p) c.addIssue({ code: "custom", message: p });
    })
    .transform(normalizeEventText);

/** `POST /fiscal-documents/{id}/cancel` (04-api: justificativa ≥ 15). */
export const cancelInvoiceInput = z.object({ reason: eventText("reason") });
export type CancelInvoiceInput = z.output<typeof cancelInvoiceInput>;

/** `POST /fiscal-documents/{id}/correction` (CC-e). */
export const correctionInput = z.object({ text: eventText("correction") });
export type CorrectionInput = z.output<typeof correctionInput>;

const nfeNumber = z
  .number("Informe o número")
  .int("Número inválido")
  .min(1, "Número de 1 a 999.999.999")
  .max(999_999_999, "Número de 1 a 999.999.999");
const nfeSeries = z
  .number("Informe a série")
  .int("Série inválida")
  .min(0, "Série de 0 a 999")
  .max(999, "Série de 0 a 999")
  .refine((s) => s < 890 || s > 899, "Séries 890 a 899 são do Fisco");

/** `POST /fiscal-documents/void-range` (inutilização; ambiente = o da empresa). */
export const voidRangeInput = z
  .object({ series: nfeSeries, from: nfeNumber, to: nfeNumber, reason: eventText("reason") })
  .superRefine((v, c) => {
    const p = voidRangeProblem(v);
    if (p) c.addIssue({ code: "custom", message: p, path: ["to"] });
  });
export type VoidRangeInput = z.output<typeof voidRangeInput>;
export const VOID_MAX_RANGE = NFE_VOID_MAX_RANGE;

export const voidOverviewQuery = z.object({ series: z.coerce.number().pipe(nfeSeries).optional() });
export type VoidOverviewQuery = z.output<typeof voidOverviewQuery>;

/** `POST /fiscal-documents/{id}/return` (devolução referenciada). */
export const returnInvoiceInput = z.object({
  items: z
    .array(
      z.object({
        position: z.number().int().min(1).max(NFE_MAX_ITEMS),
        qtyQ4: z.number().int().min(1, "Quantidade deve ser maior que zero").max(MAX_STOCK_QTY_Q4),
      }),
    )
    .min(1, "Escolha ao menos um item")
    .max(NFE_MAX_ITEMS)
    .refine((l) => new Set(l.map((i) => i.position)).size === l.length, "Item repetido"),
  reason: text(500),
});
export type ReturnInvoiceInput = z.output<typeof returnInvoiceInput>;

export type FiscalEventView = {
  id: string;
  kind: FiscalEventKind;
  fiscalDocumentId: string | null;
  sequence: number;
  text: string | null;
  status: FiscalEventStatus;
  protocol: string | null;
  statusCode: string | null;
  statusMessage: string | null;
  environment: 1 | 2;
  voidSeries: number | null;
  voidFrom: number | null;
  voidTo: number | null;
  hasXml: boolean;
  hasPdf: boolean;
  /** "Consultar de novo" disponível: a rodada terminou pendente ou ficou parada. */
  canRefresh: boolean;
  registeredAt: string | null;
  createdAt: string;
};

/** Tela de inutilização: lacunas da série no ambiente da empresa e histórico das inutilizações. */
export type NfeVoidOverview = {
  environment: 1 | 2;
  series: number;
  nextNumber: number;
  gaps: NfeGap[];
  events: FiscalEventView[];
};
